import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Target, ChevronDown, ChevronUp, CalendarIcon, Trash2, ImageIcon, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";
import { uploadAndGetSignedUrl, refreshSignedUrls, validateFileSize, compressImage } from "@/lib/storage";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const categoryColors: Record<string, string> = {
  Financial: "bg-sage-light text-primary",
  Health: "bg-terracotta-light text-accent",
  "Dating & Family": "bg-secondary text-secondary-foreground",
  Personal: "bg-card text-foreground",
};

export default function GoalsPage() {
  const { user, profile } = useAuth();
  const [goals, setGoals] = useState<any[]>([]);
  const [expandedGoal, setExpandedGoal] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: "", description: "", category: "Personal", targetDate: undefined as Date | undefined });
  const [loading, setLoading] = useState(true);

  const workspaceId = profile?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;
    supabase
      .from("goals")
      .select("*, key_results(*)")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          toast.error("Couldn't load goals", { description: error.message });
        }
        setGoals(data || []);
        setLoading(false);
      });
  }, [workspaceId]);

  const addGoal = async () => {
    if (!newGoal.title.trim() || !workspaceId) return;
    try {
      const { data, error } = await supabase
        .from("goals")
        .insert({
          workspace_id: workspaceId,
          created_by: user!.id,
          title: newGoal.title.trim(),
          description: newGoal.description.trim() || null,
          category: newGoal.category,
          timebound: newGoal.targetDate ? format(newGoal.targetDate, "MMM yyyy") : null,
          target_date: newGoal.targetDate ? format(newGoal.targetDate, "yyyy-MM-dd") : null,
        })
        .select("*, key_results(*)")
        .single();
      if (error) {
        toast.error("Couldn't create goal", { description: error.message });
        return;
      }
      setGoals([data, ...goals]);
      setNewGoal({ title: "", description: "", category: "Personal", targetDate: undefined });
      setIsAdding(false);
      toast.success("Goal created ✨");
      logActivity("goal_created", { goal_id: data.id, category: data.category });

      // Fire notification async
      const displayName = profile?.preferred_name || profile?.full_name || "Your partner";
      supabase.functions.invoke("send-notification-email", {
        body: {
          type: "goal_added",
          workspaceId,
          actorName: displayName,
          details: { goalTitle: data.title, category: data.category, description: data.description },
        },
      }).catch(console.error);
    } catch (e: any) {
      toast.error("Couldn't create goal", { description: e.message });
    }
  };

  const deleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase.from("goals").delete().eq("id", goalId);
      if (error) {
        toast.error("Couldn't delete", { description: error.message });
        return;
      }
      setGoals(goals.filter((g) => g.id !== goalId));
      setExpandedGoal(null);
      toast.success("Goal removed");
    } catch (e: any) {
      toast.error("Couldn't delete", { description: e.message });
    }
  };

  const uploadGoalMedia = async (goalId: string, file: File) => {
    if (!workspaceId) return;
    const sizeErr = validateFileSize(file);
    if (sizeErr) { toast.error(sizeErr); return; }
    const ext = file.name.split(".").pop();
    const path = `${workspaceId}/goals/${goalId}-${Date.now()}.${ext}`;
    const { signedUrl, error: upErr } = await uploadAndGetSignedUrl("couple-uploads", path, file);
    if (upErr || !signedUrl) {
      toast.error("Upload failed", { description: upErr || "Unknown error" });
      return;
    }
    const { error } = await supabase.from("goal_attachments").insert({
      goal_id: goalId,
      file_url: path,
      file_type: file.type.startsWith("image") ? "image" : "file",
      uploaded_by: user!.id,
    });
    if (error) {
      toast.error("Couldn't save", { description: error.message });
    } else {
      toast.success("Media added ✨");
      loadGoalAttachments(goalId);
    }
  };

  const [goalAttachments, setGoalAttachments] = useState<Record<string, any[]>>({});

  const loadGoalAttachments = async (goalId: string) => {
    const { data } = await supabase
      .from("goal_attachments")
      .select("*")
      .eq("goal_id", goalId)
      .order("created_at", { ascending: false });
    const withUrls = await refreshSignedUrls(data || [], "file_url");
    setGoalAttachments((prev) => ({ ...prev, [goalId]: withUrls }));
  };

  useEffect(() => {
    if (expandedGoal) loadGoalAttachments(expandedGoal);
  }, [expandedGoal]);

  // KR state
  const [addingKR, setAddingKR] = useState<string | null>(null);
  const [newKR, setNewKR] = useState({ title: "", targetValue: "", unit: "" });

  const addKeyResult = async (goalId: string) => {
    if (!newKR.title.trim()) return;
    try {
      const { data, error } = await supabase
        .from("key_results")
        .insert({
          goal_id: goalId,
          title: newKR.title.trim(),
          target_value: newKR.targetValue ? parseFloat(newKR.targetValue) : null,
          unit: newKR.unit.trim() || null,
          current_value: 0,
          progress: 0,
        })
        .select()
        .single();
      if (error) {
        toast.error("Couldn't add key result", { description: error.message });
        return;
      }
      setGoals(goals.map((g) =>
        g.id === goalId ? { ...g, key_results: [...(g.key_results || []), data] } : g
      ));
      setNewKR({ title: "", targetValue: "", unit: "" });
      setAddingKR(null);
    } catch (e: any) {
      toast.error("Couldn't add key result", { description: e.message });
    }
  };

  const updateKRValue = async (krId: string, goalId: string, currentValue: number, targetValue: number | null) => {
    const progress = targetValue && targetValue > 0 ? Math.min(100, Math.round((currentValue / targetValue) * 100)) : 0;
    await supabase.from("key_results").update({ current_value: currentValue, progress }).eq("id", krId);
    setGoals(goals.map((g) =>
      g.id === goalId
        ? { ...g, key_results: g.key_results.map((kr: any) => kr.id === krId ? { ...kr, current_value: currentValue, progress } : kr) }
        : g
    ));
    logActivity("kr_updated", { kr_id: krId, goal_id: goalId, current_value: currentValue, progress });
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Our Goals</h1>
        <p className="mt-2 text-muted-foreground text-lg">Intentional commitments we're pursuing together.</p>
      </motion.div>

      {/* Add Goal */}
      {isAdding ? (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
          <input value={newGoal.title} onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })} placeholder="Goal title..." className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          <textarea value={newGoal.description} onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })} placeholder="Why does this matter to us?" className="w-full bg-background border border-input rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-ring/40" />
          <div className="flex gap-3 flex-wrap items-center">
            <select value={newGoal.category} onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })} className="bg-background border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
              <option>Financial</option>
              <option>Health</option>
              <option>Dating & Family</option>
              <option>Personal</option>
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 bg-background border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  {newGoal.targetDate ? format(newGoal.targetDate, "MMM d, yyyy") : "Set timeline"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={newGoal.targetDate}
                  onSelect={(date) => setNewGoal({ ...newGoal, targetDate: date })}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex gap-2">
            <button onClick={addGoal} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90">Create Goal</button>
            <button onClick={() => setIsAdding(false)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium">Cancel</button>
          </div>
        </motion.div>
      ) : (
        <button onClick={() => setIsAdding(true)} className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors">
          <Plus className="w-4 h-4" /> Add a Goal
        </button>
      )}

      {/* Goals List */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading goals...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {goals.map((goal, i) => {
            const krs = goal.key_results || [];
            const avgProgress = krs.length > 0
              ? Math.round(krs.reduce((s: number, kr: any) => s + (kr.progress || 0), 0) / krs.length)
              : 0;

            return (
              <motion.div key={goal.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} className="bg-card border border-border rounded-xl overflow-hidden">
                <button onClick={() => setExpandedGoal(expandedGoal === goal.id ? null : goal.id)} className="w-full p-6 flex items-start justify-between text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[goal.category] || "bg-card text-foreground"}`}>{goal.category}</span>
                      {goal.target_date && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarIcon className="w-3 h-3" />
                          {format(new Date(goal.target_date), "MMM d, yyyy")}
                        </span>
                      )}
                      {krs.length > 0 && (
                        <span className="text-xs text-muted-foreground">{avgProgress}% complete</span>
                      )}
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground truncate">{goal.title}</h3>
                    {goal.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{goal.description}</p>}
                  </div>
                  <div className="ml-4 mt-1 flex-shrink-0">
                    {expandedGoal === goal.id ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                  </div>
                </button>

                <AnimatePresence>
                  {expandedGoal === goal.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="px-6 pb-6 space-y-3 border-t border-border pt-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                            <Target className="w-3.5 h-3.5" /> Key Results
                          </h4>
                          <button
                            onClick={() => deleteGoal(goal.id)}
                            className="flex items-center gap-1.5 text-xs text-destructive hover:underline"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>

                        {(goal.key_results || []).length === 0 && addingKR !== goal.id ? (
                          <p className="text-sm text-muted-foreground italic">No key results yet. Add measurable outcomes.</p>
                        ) : (
                          (goal.key_results || []).map((okr: any) => (
                            <div key={okr.id} className="bg-background rounded-lg p-4 space-y-2">
                              <div className="flex justify-between items-start text-sm">
                                <span className="font-medium text-foreground flex-1 min-w-0 truncate">{okr.title}</span>
                                {okr.target_value != null && (
                                  <span className="text-muted-foreground ml-2 flex-shrink-0">{okr.progress || 0}%</span>
                                )}
                              </div>
                              {okr.target_value != null ? (
                                <div className="space-y-2">
                                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(100, okr.progress || 0)}%` }}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      value={okr.current_value || 0}
                                      onChange={(e) => updateKRValue(okr.id, goal.id, parseFloat(e.target.value) || 0, okr.target_value)}
                                      className="w-20 bg-card border border-input rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-ring"
                                    />
                                    <span className="text-xs text-muted-foreground">/ {okr.target_value} {okr.unit || ""}</span>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">No target set</p>
                              )}
                              {okr.note && <p className="text-xs text-muted-foreground italic">"{okr.note}"</p>}
                            </div>
                          ))
                        )}

                        {addingKR === goal.id ? (
                          <div className="space-y-3 bg-background rounded-lg p-4">
                            <input value={newKR.title} onChange={(e) => setNewKR({ ...newKR, title: e.target.value })} placeholder="Key result title..." className="w-full bg-card border border-input rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                            <div className="flex gap-2 flex-wrap">
                              <input
                                type="number"
                                value={newKR.targetValue}
                                onChange={(e) => setNewKR({ ...newKR, targetValue: e.target.value })}
                                placeholder="Target (e.g. 10000)"
                                className="flex-1 min-w-[120px] bg-card border border-input rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                              />
                              <input
                                value={newKR.unit}
                                onChange={(e) => setNewKR({ ...newKR, unit: e.target.value })}
                                placeholder="Unit (e.g. steps)"
                                className="flex-1 min-w-[100px] bg-card border border-input rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => addKeyResult(goal.id)} className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm">Add</button>
                              <button onClick={() => { setAddingKR(null); setNewKR({ title: "", targetValue: "", unit: "" }); }} className="px-3 py-2 bg-secondary rounded-lg text-sm">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setAddingKR(goal.id)} className="text-sm text-primary hover:underline flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> Add Key Result
                          </button>
                        )}

                        {/* Media attachments */}
                        <div className="pt-2 border-t border-border space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-muted-foreground">Attachments</p>
                            <label className="flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
                              <ImageIcon className="w-3.5 h-3.5" /> Add media
                              <input type="file" accept="image/*,video/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadGoalMedia(goal.id, f); }} className="hidden" />
                            </label>
                          </div>
                          {(goalAttachments[goal.id] || []).length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {goalAttachments[goal.id].map((att: any) => (
                                <img key={att.id} src={att.file_url} alt="" className="w-20 h-20 rounded-lg object-cover max-w-full" loading="lazy" />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
